import { createContext, useEffect, useState, useMemo } from 'react';
import update from 'immutability-helper';
import identity from 'lodash/identity';
import curryRight from 'lodash/curryRight';
import mapValues from 'lodash/fp/mapValues';
import getOr from 'lodash/fp/getOr';
import {
  PostMessageConfigSet,
  PostMessageConfig,
  PostMessageEvent,
} from './types';

const curriedUpdate = curryRight(update, 2);

const dispatchMessage = (payload: PostMessageEvent) => {
  if (window.parent === window) {
    // eslint-disable-next-line no-console
    console.log('cannot request to parent window', payload);
    return;
  }
  window.parent.postMessage(payload, '*');
};

const PostMessageContext = createContext<
  [Record<string, any>, Record<string, Function>]
>([{}, {}]);

const createInitialState = mapValues<PostMessageConfig<unknown>, unknown>(
  getOr(null, ['initialValue']),
);

const createHandlers = mapValues<PostMessageConfig<unknown>, Function>(
  ({ dispatch }) =>
    (...args: Parameters<typeof dispatch>) =>
      dispatchMessage(dispatch(...args)),
);

const createUpdater = curryRight(
  ({ data }: { data: any }, config: PostMessageConfigSet) =>
    mapValues(
      ({ parse }) =>
        typeof parse === 'function' ? parse(data) : { $apply: identity },
      config,
    ),
);

const Provider = <TConfig extends PostMessageConfigSet>({
  config,
  children,
}: {
  config: TConfig;
  children: React.ReactElement;
}) => {
  const [state, setState] = useState(createInitialState(config));
  const handlers = useMemo(() => createHandlers(config), [config]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      setState(curriedUpdate(createUpdater(event, config)));
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });
  return (
    <PostMessageContext.Provider value={[state, handlers]}>
      {children}
    </PostMessageContext.Provider>
  );
};

export default PostMessageContext;
export { Provider };
